import React, { useCallback, useEffect, useMemo, useState } from "react";
import RelationshipTargets from "./RelationshipTargets";
import TaskPanel, { cleanTitle, firstName, plusDays, todayIso, KIND_LABEL } from "./TaskPanel";
import FormDialog from "./FormDialog";
import type { Contact, Deal, Partner, Principal, Todo } from "../types";
import { useData } from "../hooks/useData";
import { api } from "../api/client";
import { I } from "./Icons";
import { Avatar, clsx, fmtDate } from "./Primitives";

interface TasksProps {
  go: (screen: string, id?: string) => void;
  openAI: (prefill?: string) => void;
  bump: () => void;
  partners: Record<string, Partner>;
  myId: string;
  focusId?: string;
  /** Phone: relationship targets have their own screen (More), so they aren't repeated under the tasks. */
  phone?: boolean;
}

interface Flag {
  key: string; type: string; severity: "high" | "med" | "low"; message: string; task_ids: string[];
  fix?: { label: string; action: string; payload?: Record<string, unknown> };
}

const FOCUS_MAX = 5;
const FLAG_NAME: Record<string, [string, string]> = {
  expired: ["expired deadline", "expired deadlines"], duplicate: ["possible duplicate", "possible duplicates"],
  likely_done: ["likely done", "likely done"], missing_dependency: ["missing dependency", "missing dependencies"],
  combined: ["to split", "to split"], monitor: ["watch, not a to-do", "watches, not to-dos"], stale: ["long overdue", "long overdue"],
  no_date: ["without a date", "without a date"], no_email: ["missing email", "missing emails"], owner_conflict: ["owner mismatch", "owner mismatches"],
  unlinked_contact: ["contact to link", "contacts to link"],
  from_notes: ["to fill from notes", "to fill from notes"], deal_link: ["deal to link", "deals to link"],
  deal_mismatch: ["deal link to check", "deal links to check"],
};

type Bucket = { key: string; label: string; hint?: string; rows: Todo[]; tag?: string };

export default function Tasks({ go, openAI, bump, partners, myId, focusId, phone }: TasksProps) {
  const [owner, setOwner] = useState<string>(() => myId || "all");
  const [view, setView] = useState<"focus" | "all">("focus");
  const [showClosed, setShowClosed] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<string | null>(focusId || null);
  const [open, setOpen] = useState<Record<string, boolean>>({ week: true });
  const [todayAll, setTodayAll] = useState(false);
  const [dialog, setDialog] = useState<null | "new" | "notes">(null);
  const [checkup, setCheckup] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [undo, setUndo] = useState<{ id: string; label: string; revert?: () => Promise<unknown> } | null>(null);
  const [datesOpen, setDatesOpen] = useState(false);

  const { data: rows, refetch } = useData<Todo[]>(`/api/todos${view === "all" && showClosed ? "?closed_days=90" : ""}`);
  const { data: flags, refetch: refetchFlags } = useData<Flag[]>(`/api/todos/hygiene?owner=${owner}`);
  const { data: deals } = useData<Deal[]>("/api/deals");
  const { data: contacts } = useData<Contact[]>("/api/contacts");
  const { data: principals } = useData<Principal[]>("/api/principals");

  useEffect(() => { if (focusId) setSelected(focusId); }, [focusId]);

  const refresh = useCallback(() => { refetch(); refetchFlags(); bump(); }, [refetch, refetchFlags, bump]);
  const colorOf = useMemo(() => {
    const m: Record<string, string> = {};
    (principals || []).forEach(p => { m[p.name] = p.color; });
    return m;
  }, [principals]);

  const all = rows || [];
  const T = todayIso();
  const weekEnd = plusDays(7);
  const mine = (t: Todo) => owner === "all" || t.owner === owner || t.owner === "all" || (t.co_owners || []).includes(owner);
  const hasOpenKids = (t: Todo) => (t.child_count || 0) > (t.child_closed || 0);

  const b = useMemo(() => {
    const vis = all.filter(mine);
    // Hand-overs not yet accepted wait in their own section.
    const active = vis.filter(t => t.status === "open" && !t.suggested && !hasOpenKids(t) && t.handoff !== "pending");
    const snoozed = active.filter(t => t.snoozed_until && t.snoozed_until > T);
    const live = active.filter(t => !(t.snoozed_until && t.snoozed_until > T));
    // A reply that came in brings a waiting task back at once; otherwise it returns on its chase date.
    const chaseNow = vis.filter(t => t.status === "waiting" && (t.reply_email_id || (t.chase_on && t.chase_on <= T)));
    const rank = (t: Todo) => (t.reply_email_id ? -1 : t.due && t.due < T ? 0 : t.status === "waiting" ? 1 : t.priority === "high" ? 2 : 3);
    const today = [...live.filter(t => t.due && t.due <= T), ...chaseNow]
      .sort((x, y) => rank(x) - rank(y) || (x.due || x.chase_on || "").localeCompare(y.due || y.chase_on || ""));
    const byDue = (x: Todo, y: Todo) => (x.due || "").localeCompare(y.due || "") || (x.priority === "high" ? -1 : 0);
    return {
      today,
      week: live.filter(t => t.due && t.due > T && t.due <= weekEnd).sort(byDue),
      later: live.filter(t => t.due && t.due > weekEnd).sort(byDue),
      nodate: live.filter(t => !t.due),
      snoozed,
      waiting: vis.filter(t => t.status === "waiting" && !t.reply_email_id && !(t.chase_on && t.chase_on <= T)),
      handoffs: vis.filter(t => t.handoff === "pending" && (t.status === "open" || t.status === "waiting") && (owner === "all" || t.owner === owner)),
      proposed: vis.filter(t => t.due_confidence === "proposed" && t.due && (t.status === "open" || t.status === "waiting") && !t.suggested)
        .sort((x, y) => (x.due || "").localeCompare(y.due || "")),
      suggested: vis.filter(t => t.status === "open" && t.suggested),
      monitoring: vis.filter(t => t.status === "monitor"),
      parents: vis.filter(t => t.status === "open" && !t.suggested && hasOpenKids(t)),
      closed: vis.filter(t => t.status === "done" || t.status === "dropped"),
      openCount: vis.filter(t => (t.status === "open" || t.status === "waiting") && !t.suggested && !hasOpenKids(t)).length,
      overdue: live.filter(t => t.due && t.due < T).length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, owner, T]);

  const searched = useMemo(() => {
    const s = q.trim().toLowerCase();
    const vis = all.filter(mine);
    if (!s) return vis;
    return vis.filter(t => `${t.task} ${t.external_code || ""} ${t.note || ""} ${t.deal_name || ""} ${t.principal_name || ""} ${t.with_text || ""} ${(t.contact_names || []).join(" ")}`
      .toLowerCase().includes(s));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, owner, q]);

  // What's shown, in order — for ↑↓ stepping through tasks from the panel.
  const focusBuckets: Bucket[] = [
    { key: "today", label: "Today", rows: todayAll ? b.today : b.today.slice(0, FOCUS_MAX) },
    { key: "week", label: "This week", rows: b.week },
    { key: "waiting", label: "Waiting on others", hint: "back on their chase date", rows: b.waiting },
    { key: "later", label: "Later", rows: b.later, tag: b.later.length ? `${fmtDate(b.later[0].due!)} – ${fmtDate(b.later[b.later.length - 1].due!)}` : undefined },
    { key: "nodate", label: "No due date", hint: "give each a date, or park it", rows: b.nodate },
    { key: "parents", label: "Split into sub-tasks", hint: "the sub-tasks show up by their own dates", rows: b.parents },
    { key: "snoozed", label: "Snoozed", rows: b.snoozed },
    { key: "monitoring", label: "Monitoring", hint: "watches, not to-dos", rows: b.monitoring, tag: "from Radar" },
  ];
  const allBuckets: Bucket[] = [
    { key: "a-open", label: "Open", rows: searched.filter(t => t.status === "open" && !t.suggested).sort((x, y) => (x.due || "9").localeCompare(y.due || "9")) },
    { key: "a-waiting", label: "Waiting on others", rows: searched.filter(t => t.status === "waiting") },
    { key: "a-suggested", label: "Suggested", rows: searched.filter(t => t.status === "open" && t.suggested) },
    { key: "a-monitor", label: "Monitoring", rows: searched.filter(t => t.status === "monitor") },
    ...(showClosed ? [{ key: "a-closed", label: "Done & dropped (90 days)", rows: searched.filter(t => t.status === "done" || t.status === "dropped") }] : []),
  ];
  const isOpen = (k: string) => view === "all" || k === "today" || !!open[k];
  const ordered = (view === "focus" ? [{ key: "handoffs", label: "", rows: b.handoffs }, ...focusBuckets.filter(x => isOpen(x.key)), { key: "suggested", label: "", rows: b.suggested }] : allBuckets)
    .flatMap(x => x.rows.map(r => r.id));
  const step = (dir: 1 | -1) => {
    if (!ordered.length) return;
    const i = selected ? ordered.indexOf(selected) : -1;
    setSelected(ordered[(i + dir + ordered.length) % ordered.length] ?? ordered[0]);
  };

  const toggleDone = async (t: Todo) => {
    await api.put(`/api/todos/${t.id}`, { status: t.status === "done" ? "open" : "done" });
    setUndo(null);
    setNotice(t.status === "done" ? "Reopened" : `Done: ${cleanTitle(t).slice(0, 60)}`);
    refresh();
  };

  // Phone: swipe left on a row to snooze it to tomorrow.
  const snooze = async (t: Todo) => {
    const prev = t.snoozed_until || null;
    try {
      await api.put(`/api/todos/${t.id}`, { snoozed_until: plusDays(1) });
      setUndo({ id: t.id, label: `Snoozed to tomorrow: ${cleanTitle(t).slice(0, 40)}`, revert: () => api.put(`/api/todos/${t.id}`, { snoozed_until: prev }) });
    } catch (e) { setNotice(e instanceof Error ? e.message : String(e)); }
    refresh();
  };

  const flagCounts = useMemo(() => {
    const c: Record<string, number> = {};
    (flags || []).forEach(f => { c[f.type] = (c[f.type] || 0) + 1; });
    return Object.entries(c).sort((x, y) => y[1] - x[1]);
  }, [flags]);
  const ownerLabel = owner === "all" ? "Everyone" : firstName(partners, owner);

  return (
    <div className={clsx("screen tasks-screen", selected && "with-panel")}>
      <div className="tasks-main">
        <div className="page-head">
          <div>
            <h1 className="page-title">Tasks</h1>
            <div className="page-sub">To-do list · shared across the three partners</div>
          </div>
          <div className="tk-stats">
            <div className="tk-stat"><span>Open</span><b>{b.openCount}</b></div>
            <div className="tk-stat"><span>Today</span><b>{b.today.length}</b></div>
            <div className="tk-stat"><span>Waiting</span><b>{b.waiting.length + b.today.filter(t => t.status === "waiting").length || "–"}</b></div>
            <div className={clsx("tk-stat", b.overdue > 0 && "bad")}><span>Overdue</span><b>{b.overdue}</b></div>
          </div>
        </div>

        <div className="tk-toolbar">
          <div className="owner-chips">
            <button className={clsx("owner-chip", owner === "all" && "active")} onClick={() => setOwner("all")}>Everyone</button>
            {Object.values(partners).filter(p => p.active !== false).map(p => (
              <button key={p.id} className={clsx("owner-chip", owner === p.id && "active")} onClick={() => setOwner(p.id)}>
                <Avatar partner={p} size={18} />{p.name.split(" ")[0]}
              </button>
            ))}
          </div>
          <div className="tk-tools">
            <button className="btn ghost sm" onClick={() => setDialog("notes")} title="Paste minutes, meeting notes or a follow-up list">✦ From notes</button>
            <button className="btn ghost sm" onClick={() => openAI && openAI("Add a task: ")}>{I.ai({ size: 13 })} Ask Saga</button>
            <button className="btn primary sm" onClick={() => setDialog("new")}>{I.plus({ size: 13, w: 2 })} New task</button>
            <div className="seg sm">
              <button className={clsx("seg-btn", view === "focus" && "active")} onClick={() => setView("focus")}>Focus</button>
              <button className={clsx("seg-btn", view === "all" && "active")} onClick={() => setView("all")}>All</button>
            </div>
          </div>
        </div>

        {undo ? (
          <div className="tk-notice undo">
            <span>{undo.label} ✓</span>
            <button className="tk-undo" onClick={async () => {
              try { await (undo.revert ? undo.revert() : api.post(`/api/todos/changes/${undo.id}/undo`, {})); setUndo(null); setNotice("Undone"); refresh(); }
              catch (e) { setNotice(e instanceof Error ? e.message : String(e)); setUndo(null); }
            }}>Undo</button>
            <button className="tk-x" aria-label="Dismiss" onClick={() => setUndo(null)}>×</button>
          </div>
        ) : notice && <div className="tk-notice" onAnimationEnd={() => setNotice(null)}>{notice}</div>}

        {(flags || []).length > 0 && (
          <div className={clsx("tk-checkup", checkup && "open")}>
            <button className="tk-checkup-head" onClick={() => setCheckup(v => !v)}>
              <span className="tk-spark">✦</span>
              <b>Check-up{owner !== "all" ? ` for ${ownerLabel}` : ""}:</b>
              <span>{flagCounts.map(([k, n]) => `${n} ${(FLAG_NAME[k] || [k, k])[n === 1 ? 0 : 1]}`).join(" · ")}</span>
              <span className="tk-checkup-go">{checkup ? "Hide" : "Review"}</span>
            </button>
            {checkup && (
              <div className="tk-flags">
                {(flags || []).map(f => (
                  <div key={f.key} className={clsx("tk-flag", f.severity)}>
                    <span className="tk-flag-type">{(FLAG_NAME[f.type] || [f.type])[0]}</span>
                    <button className="tk-flag-msg" onClick={() => setSelected(f.task_ids[0])}>{f.message}</button>
                    {f.fix && (
                      <button className="btn ghost sm" onClick={async () => {
                        if (f.fix!.action === "split") { setSelected(f.task_ids[0]); return; }
                        try {
                          const r = await api.post<{ change_id: string }>("/api/todos/hygiene/fix", { action: f.fix!.action, task_ids: f.task_ids, payload: f.fix!.payload || {} });
                          if (r.change_id) setUndo({ id: r.change_id, label: f.fix!.label }); else setNotice(`${f.fix!.label} ✓`);
                          refresh();
                        } catch (e) { setNotice(e instanceof Error ? e.message : String(e)); }
                      }}>{f.fix.label}</button>
                    )}
                    <button className="btn ghost sm tk-dismiss" title="Not this — don't suggest it again" onClick={async () => {
                      try {
                        await api.post("/api/dismissals", { kind: "checkup", key: f.key, label: f.message });
                        setUndo({ id: f.key, label: "Dismissed — won't be suggested again",
                          revert: () => api.delete(`/api/dismissals/checkup?key=${encodeURIComponent(f.key)}`) });
                        refresh();
                      } catch (e) { setNotice(e instanceof Error ? e.message : String(e)); }
                    }}>Dismiss</button>
                  </div>
                ))}
                <DismissedCheckups v={flags?.length || 0} onRestored={() => { setNotice("Restored"); refresh(); }} />
                <ChangesToday onUndone={() => { setNotice("Undone"); refresh(); }} />
              </div>
            )}
          </div>
        )}

        {view === "all" && (
          <div className="tk-search">
            {I.search({ size: 14 })}
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search tasks, codes (F15), people, deals…" />
            <label className="show-done"><input type="checkbox" checked={showClosed} onChange={e => setShowClosed(e.target.checked)} /> Show done & dropped</label>
          </div>
        )}

        {b.proposed.length > 0 && (
          <ProposedDates rows={b.proposed} partners={partners} open={datesOpen} setOpen={setDatesOpen}
            onDone={(id, n) => { setUndo({ id, label: n < 0 ? `Removed ${-n} proposed date${n === -1 ? "" : "s"}` : `Agreed ${n} date${n === 1 ? "" : "s"}` }); refresh(); }} />
        )}

        {view === "focus" ? (
          <>
            {b.handoffs.length > 0 && (
              <Section title="Handed to you" count={b.handoffs.length} tone="handoff"
                right={b.handoffs.length > 1 ? undefined : "take it on, or hand it back"}
                action={b.handoffs.length > 1 ? (
                  <button className="btn ghost sm" onClick={async () => {
                    const r = await api.post<{ change_id: string }>("/api/todos/bulk", { action: "accept_handoffs", ids: b.handoffs.map(t => t.id) });
                    setUndo({ id: r.change_id, label: `Accepted ${b.handoffs.length} tasks` }); refresh();
                  }}>Accept all {b.handoffs.length}</button>
                ) : undefined}>
                <Rows rows={b.handoffs} {...{ selected, setSelected, partners, colorOf, toggleDone, T }} handoff
                  onAccept={async t => { await api.post(`/api/todos/${t.id}/handoff`, { accept: true }); setNotice("On your list ✓"); refresh(); }}
                  onDismiss={t => setSelected(t.id)} />
              </Section>
            )}
            <Section title="Today" count={b.today.length} right={`${ownerLabel} · max ${FOCUS_MAX} in focus`}>
              {b.today.length === 0 ? (
                <div className="tk-empty">Nothing due today{b.week.length ? ` — ${b.week.length} coming up this week` : ""}. 🎉</div>
              ) : (
                <Rows rows={todayAll ? b.today : b.today.slice(0, FOCUS_MAX)} {...{ selected, setSelected, partners, colorOf, toggleDone, T }} onSnooze={snooze} />
              )}
              {b.today.length > FOCUS_MAX && (
                <button className="tk-more" onClick={() => setTodayAll(v => !v)}>
                  {todayAll ? "Show only the top 5" : `+ ${b.today.length - FOCUS_MAX} more due — show all`}
                </button>
              )}
            </Section>

            {b.suggested.length > 0 && (
              <Section title="Suggested" count={b.suggested.length} right="AI & Radar ideas — accept with an owner and date, or dismiss" tone="sug">
                <Rows rows={b.suggested} {...{ selected, setSelected, partners, colorOf, toggleDone, T }} suggested
                  onAccept={async (t, d) => { await api.post(`/api/todos/${t.id}/accept`, { owner: t.owner, due: d }); setNotice("On the list ✓"); refresh(); }}
                  onDismiss={async t => { await api.put(`/api/todos/${t.id}`, { status: "dropped", dropped_reason: "Suggestion not taken up", suggested: false }); setNotice("Dismissed"); refresh(); }} />
              </Section>
            )}

            <div className="tk-rest-h"><span>The rest</span><span className="tk-rest-sub">collapsed — click to open</span></div>
            <div className="tk-rest">
              {focusBuckets.slice(1).filter(x => x.rows.length).map(x => (
                <div key={x.key} className={clsx("tk-fold", isOpen(x.key) && "open")}>
                  <button className="tk-fold-head" onClick={() => setOpen(o => ({ ...o, [x.key]: !o[x.key] }))}>
                    <span className="tk-chev">{isOpen(x.key) ? "▾" : "›"}</span>
                    <span className="tk-fold-t">{x.label}</span>
                    <span className="tk-n">{x.rows.length}</span>
                    {!isOpen(x.key) && <span className="tk-fold-preview">{x.rows.slice(0, 3).map(r => (r.external_code ? `${r.external_code} ` : "") + cleanTitle(r)).join(" · ")}</span>}
                    {isOpen(x.key) && x.hint && <span className="tk-fold-preview">{x.hint}</span>}
                    {x.tag && <span className="tk-fold-tag">{x.tag}</span>}
                  </button>
                  {isOpen(x.key) && <Rows rows={x.rows} {...{ selected, setSelected, partners, colorOf, toggleDone, T }} onSnooze={snooze} />}
                </div>
              ))}
            </div>
          </>
        ) : (
          allBuckets.filter(x => x.rows.length).map(x => (
            <Section key={x.key} title={x.label} count={x.rows.length}>
              <Rows rows={x.rows} {...{ selected, setSelected, partners, colorOf, toggleDone, T }} onSnooze={snooze} />
            </Section>
          ))
        )}

        {!phone && <RelationshipTargets partners={partners} go={go} />}
      </div>

      {selected && (
        <TaskPanel id={selected} partners={partners} deals={deals || []} contacts={contacts || []} myId={myId}
          go={(screen, id) => (screen === "tasks" && id ? setSelected(id) : go(screen, id))}
          onClose={() => setSelected(null)} onChanged={refresh} onStep={step} />
      )}

      {dialog === "new" && (
        <FormDialog
          title="New task"
          initial={{ task: "", owner: owner === "all" ? myId : owner, due: "", priority: "med", kind: "", deal_id: "", with_text: "", note: "" }}
          fields={[
            { key: "task", label: "What needs doing", required: true, wide: true, placeholder: "e.g. Send the Portuguese follow-up to ANPG" },
            { key: "owner", label: "Owner", type: "select", required: true, options: Object.values(partners).filter(p => p.active !== false).map(p => ({ value: p.id, label: p.name })) },
            { key: "due", label: "Due", type: "date" },
            { key: "priority", label: "Priority", type: "select", required: true, options: [{ value: "high", label: "High" }, { value: "med", label: "Normal" }, { value: "low", label: "Low" }] },
            { key: "kind", label: "Type", type: "select", options: Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label })) },
            { key: "deal_id", label: "Deal", type: "select", wide: true, options: [...(deals || [])].sort((x, y) => x.name.localeCompare(y.name)).map(d => ({ value: d.id, label: d.name })) },
            { key: "with_text", label: "With (who's on the other side)", wide: true, placeholder: "Link contacts in the task afterwards" },
            { key: "note", label: "Context", type: "textarea" },
          ]}
          saveLabel="Add task"
          onClose={() => setDialog(null)}
          onSave={async v => {
            const t = await api.post<Todo>("/api/todos", {
              ...v, due: v.due || null, kind: v.kind || null, deal_id: v.deal_id || null, with_text: v.with_text || null, source_type: "manual",
            });
            setDialog(null); refresh(); setSelected(t.id);
          }}
        />
      )}
      {dialog === "notes" && <NotesDialog deals={deals || []} onClose={() => setDialog(null)} onDone={msg => { setDialog(null); setNotice(msg); refresh(); }} />}
    </div>
  );
}

function Section({ title, count, right, children, tone, action }: {
  title: string; count: number; right?: string; children: React.ReactNode; tone?: string; action?: React.ReactNode;
}) {
  return (
    <section className={clsx("tk-sec", tone)}>
      <div className="tk-sec-h"><h2>{title}</h2><span className="tk-n">{count}</span>{right && <span className="tk-sec-r">{right}</span>}
        {action && <span className="tk-sec-a">{action}</span>}</div>
      <div className="tk-card">{children}</div>
    </section>
  );
}

/**
 * Touch swipes on a task row (phones): right = complete, left = snooze to tomorrow.
 * Vertical scrolling wins whenever the finger moves more up/down than sideways.
 */
function useSwipe(onRight?: () => void, onLeft?: () => void) {
  const [dx, setDx] = React.useState(0);
  const st = React.useRef<{ x: number; y: number; dir: "h" | "v" | null } | null>(null);
  const swiped = React.useRef(false);
  if (!onRight && !onLeft) return { dx: 0, handlers: {}, swiped };
  const handlers = {
    onTouchStart: (e: React.TouchEvent) => { const t = e.touches[0]; st.current = { x: t.clientX, y: t.clientY, dir: null }; swiped.current = false; },
    onTouchMove: (e: React.TouchEvent) => {
      const s = st.current; if (!s) return;
      const t = e.touches[0], mx = t.clientX - s.x, my = t.clientY - s.y;
      if (!s.dir && (Math.abs(mx) > 10 || Math.abs(my) > 10)) s.dir = Math.abs(mx) > Math.abs(my) ? "h" : "v";
      if (s.dir === "h") setDx(Math.max(onLeft ? -140 : 0, Math.min(onRight ? 140 : 0, mx)));
    },
    onTouchEnd: () => {
      const d = dx; st.current = null; setDx(0);
      if (d > 80 && onRight) { swiped.current = true; onRight(); }
      else if (d < -80 && onLeft) { swiped.current = true; onLeft(); }
      else if (Math.abs(d) > 10) swiped.current = true;
    },
  };
  return { dx, handlers, swiped };
}

function Rows(props: {
  rows: Todo[]; selected: string | null; setSelected: (id: string) => void; partners: Record<string, Partner>;
  colorOf: Record<string, string>; toggleDone: (t: Todo) => void; T: string;
  suggested?: boolean; handoff?: boolean; onAccept?: (t: Todo, due: string) => void; onDismiss?: (t: Todo) => void; onSnooze?: (t: Todo) => void;
}) {
  return <>{props.rows.map(t => <Row key={t.id} t={t} {...props} />)}</>;
}

function Row({ t, selected, setSelected, partners, colorOf, toggleDone, T, suggested, handoff, onAccept, onDismiss, onSnooze }: {
  t: Todo; selected: string | null; setSelected: (id: string) => void; partners: Record<string, Partner>;
  colorOf: Record<string, string>; toggleDone: (t: Todo) => void; T: string;
  suggested?: boolean; handoff?: boolean; onAccept?: (t: Todo, due: string) => void; onDismiss?: (t: Todo) => void; onSnooze?: (t: Todo) => void;
}) {
  const closedRow = t.status === "done" || t.status === "dropped";
  const canSwipe = !suggested && !handoff && !closedRow && typeof window !== "undefined" && "ontouchstart" in window;
  const { dx, handlers, swiped } = useSwipe(canSwipe ? () => toggleDone(t) : undefined, canSwipe && onSnooze && t.status === "open" ? () => onSnooze(t) : undefined);
  {
        const chase = t.status === "waiting";
        const when = chase ? t.chase_on : t.due;
        const late = !!when && when < T && (t.status === "open" || chase);
        const isToday = when === T;
        const closed = t.status === "done" || t.status === "dropped";
        return (
          <div className={clsx("tk-swipe", dx > 0 && "right", dx < 0 && "left")}>
          {dx !== 0 && <span className="tk-swipe-bg">{dx > 0 ? (dx > 80 ? "✓ Done" : "Done") : (dx < -80 ? "Tomorrow ◷" : "Snooze")}</span>}
          <div className={clsx("tk-row", selected === t.id && "sel", closed && "closed")} {...handlers}
            style={dx ? { transform: `translateX(${dx}px)`, transition: "none" } : undefined}
            onClick={() => { if (swiped.current) { swiped.current = false; return; } setSelected(t.id); }}>
            {!suggested && (
              <button className={clsx("checkbox", t.status === "done" && "checked")} aria-label="Complete"
                onClick={e => { e.stopPropagation(); toggleDone(t); }}>{t.status === "done" && I.check({ size: 13, w: 2.4 })}</button>
            )}
            {suggested && <span className="tk-sug-dot">✦</span>}
            <div className="tk-body">
              <div className="tk-title">
                {t.reply_email_id ? <span className="tk-replied">Reply from {t.reply?.from || t.waiting_on}: </span>
                  : chase && <span className="tk-chase">Chase {t.waiting_on}: </span>}
                {cleanTitle(t)}
              </div>
              <div className="tk-chips">
                {t.principal_name && <span className="tk-chip"><i className="tk-dot" style={{ background: colorOf[t.principal_name] || undefined }} />{t.principal_name}</span>}
                {t.deal_name && <span className="tk-chip soft" title="Deal">{t.deal_name}</span>}
                {t.external_code && <span className="tk-code">{t.external_code}</span>}
                {t.reply_email_id && <span className="tk-chip ok" title={t.reply?.subject || ""}>✉ Reply received — mark done?</span>}
                {t.reply_proposal && <span className="tk-chip soft">🤖 {t.reply_proposal === "done" ? "bot says it's answered" : "bot says not the answer"}</span>}
                {t.handoff === "pending" && <span className="tk-chip handoff">↪ from {t.delegated_by_name?.split(" ")[0] || (t.delegated_by || "").replace(/^bot:/, "") || "a colleague"}</span>}
                {t.status === "waiting" && !chase && <span className="tk-chip wait">⏳ Waiting on {t.waiting_on}</span>}
                {t.status === "waiting" && <span className="tk-chip wait">⏳ since {t.waiting_since ? fmtDate(t.waiting_since) : "—"}</span>}
                {t.ai?.draft && !t.ai.stale && t.status === "open" && <span className="tk-chip ai">✦ Draft ready</span>}
                {(t.child_count || 0) > 0 && <span className="tk-chip soft">{t.child_closed}/{t.child_count} sub-tasks</span>}
                {(t.blocked_by || []).length > 0 && <span className="tk-chip soft">⛓ waits on {(t.blocked_by || []).length}</span>}
                {t.due_confidence === "proposed" && t.due && <span className="tk-chip soft" title="Proposed by AI — not agreed">proposed date</span>}
                {t.status === "dropped" && <span className="tk-chip soft">dropped — {t.dropped_reason}</span>}
                {t.source_type === "radar" && !t.principal_name && <span className="tk-chip soft">Radar</span>}
                {t.parent_id && <span className="tk-chip soft">sub-task</span>}
              </div>
            </div>
            <div className="tk-meta">
              {t.priority === "high" && !closed && <span className="ti-pri high">High</span>}
              {handoff ? (
                <>
                  <button className="btn ghost sm" onClick={e => { e.stopPropagation(); onAccept?.(t, t.due || ""); }}>Accept</button>
                  <button className="btn ghost sm" onClick={e => { e.stopPropagation(); onDismiss?.(t); }} title="Open it to hand it back with a note">Hand back…</button>
                </>
              ) : suggested ? (
                <>
                  <button className="btn ghost sm" onClick={e => { e.stopPropagation(); onAccept?.(t, t.due || plusDays(7)); }}
                    title={`Put it on ${firstName(partners, t.owner)}'s list, due ${fmtDate(t.due || plusDays(7))}`}>Accept</button>
                  <button className="btn ghost sm" onClick={e => { e.stopPropagation(); onDismiss?.(t); }}>Dismiss</button>
                </>
              ) : (
                <span className={clsx("tk-due mono", late && "late", isToday && "today")}>
                  {I.clock({ size: 12 })}{when ? (isToday ? "Today" : fmtDate(when)) : "—"}
                </span>
              )}
              <span className="tk-owner"><Avatar partner={partners[t.owner] || null} size={24} />
                {!partners[t.owner] && <span className="ti-owner-tag">{firstName(partners, t.owner)}</span>}
                {(t.co_owners || []).length > 0 && <span className="tk-co">+{(t.co_owners || []).length}</span>}
              </span>
            </div>
          </div>
          </div>
        );
  }
}

function NotesDialog({ deals, onClose, onDone }: { deals: Deal[]; onClose: () => void; onDone: (msg: string) => void }) {
  const [text, setText] = useState("");
  const [label, setLabel] = useState("");
  const [deal, setDeal] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<{ created: Todo[]; duplicates: { task: string; existing: { id: string; task: string } }[] } | null>(null);
  return (
    <div className="fd-scrim" onMouseDown={onClose}>
      <div className="fd-panel" role="dialog" aria-label="Tasks from notes" onMouseDown={e => e.stopPropagation()}>
        <div className="fd-head">
          <div>
            <div className="fd-title">Tasks from notes</div>
            <div className="fd-sub">Paste minutes, meeting notes or a follow-up list. The assistant proposes tasks with owner and counterpart,
              skips anything that's already a task, and puts the rest under Suggested for you to accept.</div>
          </div>
          <button className="kbd-chip fd-esc" onClick={onClose}>esc</button>
        </div>
        <div className="fd-body">
          {!result ? (
            <div className="set-grid">
              <label className="set-field wide"><span className="set-label">Notes *</span>
                <textarea rows={12} value={text} onChange={e => setText(e.target.value)} placeholder="Paste here…" autoFocus /></label>
              <label className="set-field"><span className="set-label">Where it's from</span>
                <input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Minutes Senegal 22 Sep" /></label>
              <label className="set-field"><span className="set-label">Deal (if it's all about one)</span>
                <select value={deal} onChange={e => setDeal(e.target.value)}>
                  <option value="">—</option>
                  {[...deals].sort((a, b) => a.name.localeCompare(b.name)).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select></label>
            </div>
          ) : (
            <div className="tk-extract">
              <p><b>{result.created.length}</b> new suggestion{result.created.length === 1 ? "" : "s"} added under Suggested.</p>
              {result.created.map(t => <div key={t.id} className="tk-extract-row">✦ {t.task}</div>)}
              {result.duplicates.length > 0 && (
                <>
                  <p style={{ marginTop: 12 }}>Already on the list — not added again:</p>
                  {result.duplicates.map((d, i) => <div key={i} className="tk-extract-row dup">{d.task} <span>= {d.existing.task}</span></div>)}
                </>
              )}
            </div>
          )}
          {err && <div className="set-notice err fd-err">{err}</div>}
        </div>
        <div className="fd-foot">
          <span style={{ flex: 1 }} />
          {!result ? (
            <>
              <button className="btn ghost sm" onClick={onClose}>Cancel</button>
              <button className="btn primary sm" disabled={busy || text.trim().length < 20} onClick={async () => {
                setBusy(true); setErr(null);
                try {
                  setResult(await api.post("/api/todos/extract", { text, source_label: label || "Pasted notes", source_type: "notes", deal_id: deal || null }));
                } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
              }}>{busy ? "Reading…" : "✦ Find the tasks"}</button>
            </>
          ) : (
            <button className="btn primary sm" onClick={() => onDone(`${result.created.length} suggestion${result.created.length === 1 ? "" : "s"} added`)}>Done</button>
          )}
        </div>
      </div>
    </div>
  );
}

function ProposedDates({ rows, partners, open, setOpen, onDone }: {
  rows: Todo[]; partners: Record<string, Partner>; open: boolean; setOpen: (v: boolean) => void; onDone: (changeId: string, n: number) => void;
}) {
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [dates, setDates] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const picked = rows.filter(t => sel[t.id] !== false);
  return (
    <div className={clsx("tk-checkup dates", open && "open")}>
      <button className="tk-checkup-head" onClick={() => setOpen(!open)}>
        <span className="tk-spark">◷</span>
        <b>{rows.length} date{rows.length === 1 ? "" : "s"} proposed by AI or a bot</b>
        <span>— not agreed by anyone yet</span>
        <span className="tk-checkup-go">{open ? "Hide" : "Review"}</span>
      </button>
      {open && (
        <div className="tk-flags">
          {rows.map(t => (
            <label key={t.id} className="tk-date-row">
              <input type="checkbox" checked={sel[t.id] !== false} onChange={e => setSel({ ...sel, [t.id]: e.target.checked })} />
              <span className="tk-date-t">{t.external_code && <b>{t.external_code} </b>}{cleanTitle(t)}</span>
              <span className="tk-date-o">{firstName(partners, t.owner)}</span>
              <input type="date" value={dates[t.id] || t.due || ""} onChange={e => setDates({ ...dates, [t.id]: e.target.value })} />
            </label>
          ))}
          <div className="tk-date-foot">
            <button className="ld-link" onClick={() => setSel(Object.fromEntries(rows.map(t => [t.id, sel[t.id] === false])))}>Invert selection</button>
            <span style={{ flex: 1 }} />
            <button className="btn ghost sm" disabled={busy || !picked.length} title="Take the proposed dates off these tasks — they'll have no due date" onClick={async () => {
              setBusy(true);
              try {
                const r = await api.post<{ change_id: string }>("/api/todos/bulk", { action: "clear_dates", ids: picked.map(t => t.id) });
                onDone(r.change_id, -picked.length); setSel({}); setDates({});
              } finally { setBusy(false); }
            }}>Remove {picked.length} date{picked.length === 1 ? "" : "s"}</button>
            <button className="btn primary sm" disabled={busy || !picked.length} onClick={async () => {
              setBusy(true);
              try {
                const r = await api.post<{ change_id: string }>("/api/todos/bulk", {
                  action: "agree_dates", ids: picked.map(t => t.id),
                  payload: { dates: Object.fromEntries(picked.filter(t => dates[t.id] && dates[t.id] !== t.due).map(t => [t.id, dates[t.id]])) },
                });
                onDone(r.change_id, picked.length); setSel({}); setDates({});
              } finally { setBusy(false); }
            }}>Agree {picked.length} date{picked.length === 1 ? "" : "s"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Check-up suggestions someone said no to — they can be brought back. */
function DismissedCheckups({ v, onRestored }: { v: number; onRestored: () => void }) {
  const { data, refetch } = useData<{ key: string; label: string | null; created_at: string; by_name: string | null }[]>("/api/dismissals/checkup", [v]);
  const [open, setOpen] = useState(false);
  if (!data || !data.length) return null;
  const restore = async (key?: string) => {
    await api.delete(`/api/dismissals/checkup${key ? `?key=${encodeURIComponent(key)}` : ""}`);
    refetch(); onRestored();
  };
  return (
    <div className="tk-changes tk-dismissed">
      <button className="tk-changes-h ld-link" onClick={() => setOpen(o => !o)}>{data.length} dismissed suggestion{data.length === 1 ? "" : "s"} {open ? "▴" : "▾"}</button>
      {open && <>
        {data.map(d => (
          <div key={d.key} className="tk-change">
            <span className="tk-change-l">{d.label || d.key}</span>
            <span className="tk-change-by">{d.by_name?.split(" ")[0] || ""}</span>
            <button className="ld-link" onClick={() => restore(d.key)}>Restore</button>
          </div>
        ))}
        {data.length > 1 && <div className="tk-change"><span className="tk-change-l" /><button className="ld-link" onClick={() => restore()}>Restore all</button></div>}
      </>}
    </div>
  );
}

function ChangesToday({ onUndone }: { onUndone: () => void }) {
  const { data, refetch } = useData<{ id: string; at: string; by_name: string | null; by: string | null; label: string; undone_at: string | null }[]>("/api/todos/changes?days=1");
  if (!data || !data.length) return <div className="tk-flags-foot">Nothing changes until you click — and every fix can be undone.</div>;
  return (
    <div className="tk-changes">
      <div className="tk-changes-h">Changes today</div>
      {data.map(c => (
        <div key={c.id} className={clsx("tk-change", c.undone_at && "undone")}>
          <span className="mono">{new Date(c.at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>
          <span className="tk-change-l">{c.label}</span>
          <span className="tk-change-by">{c.by_name?.split(" ")[0] || (c.by || "").replace(/^bot:/, "🤖 ")}</span>
          {c.undone_at ? <span className="tk-change-by">undone</span> : (
            <button className="ld-link" onClick={async () => { await api.post(`/api/todos/changes/${c.id}/undo`, {}); refetch(); onUndone(); }}>Undo</button>
          )}
        </div>
      ))}
    </div>
  );
}
